import { ApiError } from "@models/error";

/**
 * Structured error format from the backend extraction service.
 */
export interface ExtractionError {
  error_code: string;
  message: string;
  suggestion: string;
}

/**
 * Attempts to parse a JSON-encoded structured error from the backend.
 * Returns null if the string is not a valid structured error.
 */
export function parseExtractionError(
  errorMessage: string | null | undefined,
): ExtractionError | null {
  if (!errorMessage) return null;
  try {
    const parsed = JSON.parse(errorMessage);
    if (parsed && typeof parsed.error_code === "string" && typeof parsed.message === "string") {
      return parsed as ExtractionError;
    }
  } catch {
    // Not a JSON error - legacy plain text
  }
  return null;
}

/**
 * Returns a user-friendly error message from a raw error_message field.
 * If the error is a structured JSON error, uses the i18n translation.
 * Falls back to a generic message for legacy plain-text errors.
 *
 * @param errorMessage - Raw error_message from the API
 * @param t - i18n translation function
 */
export function getExtractionErrorMessage(
  errorMessage: string | null | undefined,
  t: (key: string) => string,
): { message: string; suggestion: string } {
  const parsed = parseExtractionError(errorMessage);
  if (parsed) {
    const message = t(`extraction.errors.${parsed.error_code}.message`);
    const suggestion = t(`extraction.errors.${parsed.error_code}.suggestion`);
    return { message, suggestion };
  }
  // Legacy unstructured error - surface the raw backend message so callers
  // can see what actually failed; fall back to the generic message only when
  // the backend gave us nothing.
  const trimmed = errorMessage?.trim();
  return {
    message: trimmed || t("extraction.errors.fallback.message"),
    suggestion: t("extraction.errors.fallback.suggestion"),
  };
}

/**
 * Extracts a user-friendly error message from an error object
 * Prioritizes: ApiError.description > ApiError.message > Error.message > fallback
 *
 * @param error - The error to extract a message from
 * @param fallbackMessage - The fallback message if no meaningful message can be extracted
 * @returns A user-friendly error message
 */
export function getErrorMessage(
  error: unknown,
  fallbackMessage: string = "An unexpected error occurred"
): string {
  if (error instanceof ApiError) {
    return error.getUserMessage();
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
}
